// ─────────────────────────────────────────────────────────────
// Push Notifications (iter272)
//
// Shows whether Firebase is wired up on the server, lets the person
// at this device turn on browser/PWA push for themselves, and fires a
// test notification. Business-hours window (6am–5pm Mon–Fri) is
// reported straight from the backend so nobody has to guess why a
// push didn't buzz at 9pm.
// ─────────────────────────────────────────────────────────────
import { useCallback, useEffect, useState } from "react";
import { tr } from "@/i18n";
import { Bell, BellOff, Send, RefreshCw, Clock, Smartphone } from "lucide-react";
import { toast } from "sonner";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PanelHeader, PanelBody, Callout, LoadingState } from "@/components/settings/SettingsUI";
import {
  enableWebPush,
  disableWebPush,
  isWebPushConfigured,
  isWebPushSupported,
  webPushPermission,
} from "@/lib/webPush";

export default function PushNotificationsPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [permission, setPermission] = useState(webPushPermission());

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/push/status");
      setStatus(data);
    } catch {
      setStatus({ configured: false, device_count: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onEnable = async () => {
    setBusy(true);
    try {
      await enableWebPush();
      setPermission(webPushPermission());
      toast.success("Push notifications enabled on this device");
      await load();
    } catch (e) {
      toast.error(e?.message || "Could not enable push notifications");
    } finally {
      setBusy(false);
    }
  };

  const onDisable = async () => {
    setBusy(true);
    try {
      await disableWebPush();
      toast.success("This device will no longer receive push alerts");
      await load();
    } catch (e) {
      toast.error(e?.message || "Could not disable push notifications");
    } finally {
      setBusy(false);
    }
  };

  const onTest = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/push/test");
      if (data?.status === "disabled") toast.error(`Firebase not configured — ${data.reason}`);
      else if (data?.sent) toast.success(`Test push sent to ${data.sent} device(s)`);
      else toast.warning(data?.reason || "No devices registered for your account yet");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test push failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <LoadingState label="Checking push status…" testId="push-panel-loading" />;

  const serverReady = Boolean(status?.configured);
  const clientReady = isWebPushConfigured();
  const hours = status?.business_hours;

  return (
    <div data-testid="push-notifications-panel">
      <PanelHeader
        icon={Bell}
        title={tr("Push Notifications")}
        hint="Crew alerts for dispatches, notes, cancellations and reschedules"
        count={`${status?.device_count ?? 0} device${(status?.device_count ?? 0) === 1 ? "" : "s"}`}
        countTone={status?.device_count ? "accent" : "neutral"}
        actions={
          <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="push-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> {tr("Refresh")}
          </Button>
        }
      />
      <PanelBody className="space-y-3">
        <Callout tone={serverReady ? "success" : "warn"} testId="push-server-status">
          {serverReady ? (
            <>Firebase server key is loaded — the backend can send pushes.</>
          ) : (
            <>
              <strong>Firebase not configured on the server.</strong> Paste your Firebase
              service-account JSON into <code>FCM_SERVICE_ACCOUNT_JSON</code> in the backend
              environment. Setup walkthrough lives in <code>/app/memory/FIREBASE_SETUP.md</code>.
            </>
          )}
        </Callout>

        {!clientReady && (
          <Callout tone="warn" testId="push-client-status">
            <strong>Browser push not configured.</strong> The Firebase web config +
            VAPID key still need to go into the frontend environment
            (<code>REACT_APP_FIREBASE_*</code>).
          </Callout>
        )}

        {hours && (
          <Callout tone={hours.open ? "success" : "info"} icon={Clock} testId="push-business-hours">
            Quiet-hours guard: alerts only go out <strong>{hours.window}</strong> ({hours.timezone}).
            Right now the window is <strong>{hours.open ? "open" : "closed"}</strong> — {hours.reason}.
          </Callout>
        )}

        <div className="flex items-center gap-2 flex-wrap pt-1">
          {permission === "granted" ? (
            <Button variant="outline" size="sm" onClick={onDisable} disabled={busy} data-testid="push-disable-btn">
              <BellOff className="w-3.5 h-3.5 mr-1.5" /> Turn off on this device
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={onEnable}
              disabled={busy || !clientReady || !isWebPushSupported()}
              data-testid="push-enable-btn"
            >
              <Bell className="w-3.5 h-3.5 mr-1.5" /> Enable on this device
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onTest} disabled={busy} data-testid="push-test-btn">
            <Send className="w-3.5 h-3.5 mr-1.5" /> Send test push
          </Button>
        </div>

        {(status?.devices || []).length > 0 && (
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="push-device-list">
            {status.devices.map((d, i) => (
              <div key={i} className="px-3 py-2 flex items-center gap-2 text-xs text-slate-600">
                <Smartphone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span className="font-medium text-slate-800">{d.platform || "unknown"}</span>
                <span className="truncate text-slate-400">{d.device_label || ""}</span>
                <span className="ml-auto shrink-0 text-slate-400">
                  {d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </PanelBody>
    </div>
  );
}
